import React, { useState, useRef } from "react";
import { Download, Upload, X, RefreshCw, AlertTriangle, ArrowRight, Trash2, Plus, Edit3 } from "lucide-react";
import * as api from "../../api";

/* -----------------------------------------------------------------------------
 * CatalogPortability — export/import the full games+packages catalog as JSON.
 *
 * Export: streams `GET /api/admin/catalog/export` and offers a browser download.
 * Import: user picks a .json file; we first POST it with apply=false to get a
 *   diff preview (create/update/delete counts + names). After the admin
 *   confirms, we POST again with apply=true to actually mutate the catalog.
 * --------------------------------------------------------------------------- */
export function CatalogPortability({ onCatalogChanged, showToast }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // { plan, payload, fileName }
  const [applying, setApplying] = useState(false);
  const fileRef = useRef(null);

  const handleExport = async () => {
    setBusy(true);
    try {
      const data = await api.adminExportCatalog();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url;
      a.download = `ag7-catalog-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast?.("Catálogo exportado", `${data.games_count} juegos · ${data.packages_count} paquetes descargados`, "success");
    } catch (e) {
      showToast?.("Error al exportar", e.message || String(e), "error");
    } finally {
      setBusy(false);
    }
  };

  const handlePickFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.name.endsWith(".json")) {
      showToast?.("Archivo no válido", "Selecciona un archivo .json exportado desde AG7.", "error");
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      let payload;
      try {
        payload = JSON.parse(reader.result);
      } catch (_err) {
        showToast?.("JSON inválido", "El archivo no se pudo parsear.", "error");
        return;
      }
      if (!payload || !Array.isArray(payload.games)) {
        showToast?.("Formato inesperado", "El archivo no contiene una lista de juegos.", "error");
        return;
      }
      setBusy(true);
      try {
        const res = await api.adminImportCatalog(payload, false);
        setPreview({ plan: res.plan, payload, fileName: file.name });
      } catch (err) {
        showToast?.("Error al previsualizar", err.message || String(err), "error");
      } finally {
        setBusy(false);
      }
    };
    reader.readAsText(file);
  };

  const confirmApply = async () => {
    if (!preview) return;
    setApplying(true);
    try {
      const res = await api.adminImportCatalog(preview.payload, true);
      showToast?.(
        "Catálogo actualizado",
        `Creados: ${res.applied.games_created} · Actualizados: ${res.applied.games_updated} · Eliminados: ${res.applied.games_deleted}`,
        "success",
      );
      setPreview(null);
      onCatalogChanged?.();
    } catch (err) {
      showToast?.("Error al aplicar", err.message || String(err), "error");
    } finally {
      setApplying(false);
    }
  };

  const g = preview?.plan?.games;
  const p = preview?.plan?.packages;
  const willDelete = (g?.delete || 0) > 0 || (p?.delete || 0) > 0;

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap" data-testid="catalog-portability">
        <button
          onClick={handleExport}
          disabled={busy}
          data-testid="catalog-export-btn"
          className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white font-bold px-4 py-2 rounded-xl text-xs flex items-center gap-2 shadow-md"
        >
          {busy && !preview ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Exportar Catálogo
        </button>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          data-testid="catalog-import-btn"
          className="bg-amber-600 hover:bg-amber-500 disabled:opacity-40 text-white font-bold px-4 py-2 rounded-xl text-xs flex items-center gap-2 shadow-md"
        >
          <Upload className="w-4 h-4" />
          Importar Catálogo
        </button>
        <input
          type="file"
          ref={fileRef}
          accept="application/json,.json"
          className="hidden"
          onChange={handlePickFile}
          data-testid="catalog-import-file-input"
        />
      </div>

      {preview && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
          onClick={() => !applying && setPreview(null)}
          data-testid="catalog-import-preview-modal"
        >
          <div data-testid="catalog-portability-div-1"
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg bg-[#0d1117] border-2 border-amber-500/50 rounded-3xl p-6 shadow-2xl space-y-5 max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-amber-500/15 border border-amber-500/40 flex items-center justify-center">
                  <ArrowRight className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-lg font-heading font-extrabold tracking-tight text-white">Previsualizar importación</h3>
                  <p className="text-[11px] text-zinc-400 truncate max-w-[280px]" title={preview.fileName}>
                    Archivo: <span className="font-mono text-zinc-300">{preview.fileName}</span>
                  </p>
                </div>
              </div>
              <button
                onClick={() => !applying && setPreview(null)}
                data-testid="catalog-import-preview-close"
                className="text-zinc-400 hover:text-white bg-ag7-surface-2 hover:bg-zinc-700 p-2 rounded-full"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-[var(--ag7-border-soft)] bg-ag7-surface p-4">
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-2">Juegos</p>
                <ul className="space-y-1 text-sm">
                  <li className="flex justify-between items-center">
                    <span className="text-emerald-300 flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Crear</span>
                    <strong className="text-white font-mono" data-testid="import-preview-games-create">{g?.create || 0}</strong>
                  </li>
                  <li className="flex justify-between items-center">
                    <span className="text-cyan-300 flex items-center gap-1"><Edit3 className="w-3.5 h-3.5" /> Actualizar</span>
                    <strong className="text-white font-mono" data-testid="import-preview-games-update">{g?.update || 0}</strong>
                  </li>
                  <li className="flex justify-between items-center">
                    <span className="text-red-300 flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Eliminar</span>
                    <strong className="text-white font-mono" data-testid="import-preview-games-delete">{g?.delete || 0}</strong>
                  </li>
                </ul>
              </div>
              <div className="rounded-2xl border border-[var(--ag7-border-soft)] bg-ag7-surface p-4">
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-2">Paquetes</p>
                <ul className="space-y-1 text-sm">
                  <li className="flex justify-between items-center">
                    <span className="text-emerald-300 flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Crear</span>
                    <strong className="text-white font-mono" data-testid="import-preview-packages-create">{p?.create || 0}</strong>
                  </li>
                  <li className="flex justify-between items-center">
                    <span className="text-cyan-300 flex items-center gap-1"><Edit3 className="w-3.5 h-3.5" /> Actualizar</span>
                    <strong className="text-white font-mono" data-testid="import-preview-packages-update">{p?.update || 0}</strong>
                  </li>
                  <li className="flex justify-between items-center">
                    <span className="text-red-300 flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Eliminar</span>
                    <strong className="text-white font-mono" data-testid="import-preview-packages-delete">{p?.delete || 0}</strong>
                  </li>
                </ul>
              </div>
            </div>

            {(g?.delete_names?.length || 0) > 0 && (
              <div className="rounded-xl bg-red-950/40 border border-red-500/40 p-3">
                <p className="text-[10px] uppercase tracking-widest text-red-300 font-mono mb-1">Juegos a eliminar</p>
                <p className="text-xs text-red-100 leading-snug break-words">{g.delete_names.join(", ")}</p>
              </div>
            )}
            {(g?.create_names?.length || 0) > 0 && (
              <div className="rounded-xl bg-emerald-950/40 border border-emerald-500/30 p-3">
                <p className="text-[10px] uppercase tracking-widest text-emerald-300 font-mono mb-1">Juegos a crear</p>
                <p className="text-xs text-emerald-100 leading-snug break-words">{g.create_names.join(", ")}</p>
              </div>
            )}

            {willDelete && (
              <div className="flex items-start gap-2 text-[11px] text-amber-100 bg-amber-950/40 border border-amber-500/40 rounded-xl p-3">
                <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <p>
                  <strong className="text-amber-300">Atención:</strong> se eliminarán definitivamente del catálogo los juegos/paquetes indicados.
                  Los pedidos, comprobantes, clientes y historial de ventas permanecen intactos.
                </p>
              </div>
            )}

            <div className="flex items-center gap-2 pt-1">
              <button
                onClick={() => setPreview(null)}
                disabled={applying}
                data-testid="catalog-import-cancel-btn"
                className="flex-1 bg-ag7-surface-2 hover:bg-zinc-700 disabled:opacity-40 text-white font-bold py-3 rounded-xl text-xs"
              >
                Cancelar
              </button>
              <button
                onClick={confirmApply}
                disabled={applying}
                data-testid="catalog-import-confirm-btn"
                className="flex-1 bg-gradient-to-r from-amber-600 to-red-600 hover:opacity-90 disabled:opacity-40 text-white font-bold py-3 rounded-xl text-xs flex items-center justify-center gap-2 shadow-lg"
              >
                {applying
                  ? <><RefreshCw className="w-4 h-4 animate-spin" /> Aplicando…</>
                  : <><ArrowRight className="w-4 h-4" /> Confirmar importación</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
